// LLM:METADATA
// :topology: [Domain:StateManagement | Role:AppState | Pattern:Module]
// :relates-to: required-by: "All Components"
// :rationale: "Global application state management"
// :contract: pre: "App action dispatched", post: "App state updated", invariant: "State is always persisted"
// :decision_cache: "Chose Vuex module for centralized app state [APP-001]"
// LLM:END

import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_STORE_APP } from "@/constants/topology"
import { resolveLegacyApiPrefix } from "@/services/api"
import {
  hasAnyModuleAccess,
  hasAuthoritativeAccessProfile,
} from "@/security/moduleAccessAuthority"

// Set topology ID for semantic logging
setTopologyId(TOPOLOGY_STORE_APP)

const defaultApiUrl = resolveLegacyApiPrefix()
const DEV_PUBLIC_DOMAIN = "https://dev.plastinka.com"
const PROD_PUBLIC_DOMAIN = "https://plastinka.com"

let modulesListInFlight = null

function canRequestModulesInventory(rootGetters) {
  const accessProfile =
    typeof rootGetters?.getAccessProfile === "function"
      ? rootGetters.getAccessProfile()
      : null

  if (!hasAuthoritativeAccessProfile(accessProfile)) return true
  const legacyPermissions =
    typeof rootGetters?.getUserRolePermissions === "function"
      ? rootGetters.getUserRolePermissions()
      : {}
  return hasAnyModuleAccess(accessProfile, legacyPermissions)
}

function emptyModulesInventory() {
  return {
    modules: [],
    menuItems: [],
  }
}

/**
 * Resolve the public storefront origin independently from the API base URL.
 *
 * Same-origin editor API calls intentionally use `/api`, but public preview/media
 * links must still target the matching storefront host.
 */
export function resolveDefaultPublicDomain(
  hostname = typeof window !== "undefined"
    ? String(window.location?.hostname || "")
    : ""
) {
  if (hostname === "editor.plastinka.com") {
    return PROD_PUBLIC_DOMAIN
  }

  if (hostname) {
    return DEV_PUBLIC_DOMAIN
  }

  return process.env.NODE_ENV === "development"
    ? DEV_PUBLIC_DOMAIN
    : PROD_PUBLIC_DOMAIN
}

const defaultDomain = resolveDefaultPublicDomain()

function applyRuntimeMode(state, { domain, devMode }) {
  state.params.domain = domain
  state.params.apiUrl = resolveLegacyApiPrefix()
  state.params.editorUrl = ""
  state.params.devMode = devMode
}

export default {
  namespaced: false,
  state: {
    params: {
      devMode: process.env.NODE_ENV === "development",
      lang: "en",
      withLangs: false,
      domain: defaultDomain,
      apiUrl: defaultApiUrl,
      editorUrl: "https://evgenii-b-site-editor-e85d.twc1.net/",
      boxesQty: 70,
    },
    modules: [],
    menuModules: [],
    userSessionStarted: false,
  },
  getters: {
    getParams: (state) => () => state.params,
    getModules: (state) => () => state.modules,
    getMenuModules: (state) => () => state.menuModules,
    getModuleById: (state) => (id) => {
      return state.modules?.find((item) => {
        return item.module_id == id
      })
    },
    // LLM:METADATA
    // :topology: [Domain:StateManagement | Role:Getter | Pattern:DataEnrichment]
    // :relates-to: uses: "state.modules", "state.menuModules", required-by: "useBreadcrumbs"
    // :rationale: "Returns module with associated pack information for hierarchical breadcrumbs"
    // :contract: pre: "Module ID provided", post: "Returns module with pack data or null", invariant: "Always returns module object or null"
    // :decision_cache: "Chose getter pattern for reactive pack data access [STORE-001]"
    // LLM:END
    getModuleWithPack: (state) => (id) => {
      const module = state.modules?.find((m) => m.module_id == id || m.id == id)
      if (!module) return null

      // Find pack info from menuModules (menuItems).
      // Primary: explicit module_pack_id mapping when backend provides it.
      let pack = state.menuModules?.find(
        (mi) => String(mi.pack_id) === String(module.module_pack_id)
      )

      // Fallback: infer pack from nested menu item membership (url/module_name).
      if (!pack) {
        pack = state.menuModules?.find((mi) => {
          if (!Array.isArray(mi.moreItems)) return false
          return mi.moreItems.some((item) => {
            const itemUrl = item?.url
            const itemModuleName = item?.module_name
            return (
              String(itemUrl) === String(module.module_id ?? module.id) ||
              (itemModuleName && itemModuleName === module.module_name)
            )
          })
        })
      }

      return {
        ...module,
        pack: pack || null,
      }
    },
  },
  mutations: {
    SET_PARAMS(state, p) {
      state.params = p
    },
    UPDATE_API_URL_FROM_ENV(state) {
      const defaultDomain = resolveDefaultPublicDomain()
      state.params.apiUrl = resolveLegacyApiPrefix()
      state.params.domain = defaultDomain
    },
    SET_MODULES_LIST(state, data) {
      state.menuModules = data.menuItems
      state.modules = data.modules
    },
    SET_DEV_MODE(state) {
      // Non-production editor domains still preview the dev public shop, while API calls stay same-origin.
      applyRuntimeMode(state, {
        domain: DEV_PUBLIC_DOMAIN,
        devMode: true,
      })
    },
    SET_EDITOR_DEV_MODE(state) {
      // Same-origin: requests go to editor.dev.plastinka.com/api/... (no CORS).
      // Public "view on site" links must still open the dev storefront, not the editor host.
      applyRuntimeMode(state, {
        domain: DEV_PUBLIC_DOMAIN,
        devMode: true,
      })
    },
    SET_EDITOR_PROD_MODE(state) {
      // Same-origin: requests go to editor.plastinka.com/api/... (no CORS).
      // Public "view on site" links must stay on the production storefront.
      applyRuntimeMode(state, {
        domain: PROD_PUBLIC_DOMAIN,
        devMode: false,
      })
    },
    SET_LOCAL_MODE(state) {
      // Local editor sessions proxy API calls, but public-preview affordances should target the dev storefront.
      applyRuntimeMode(state, {
        domain: DEV_PUBLIC_DOMAIN,
        devMode: true,
      })
    },
  },
  actions: {
    setParams({ commit }, newValue) {
      commit("SET_PARAMS", newValue)
    },
    setModulesList({ commit }, newValue) {
      commit("SET_MODULES_LIST", newValue)
    },
    ensureModulesList({ state, commit, rootGetters }) {
      if (!canRequestModulesInventory(rootGetters)) {
        const emptyInventory = emptyModulesInventory()
        const hasStaleInventory =
          (Array.isArray(state.modules) && state.modules.length > 0) ||
          (Array.isArray(state.menuModules) && state.menuModules.length > 0)

        if (hasStaleInventory) {
          commit("SET_MODULES_LIST", emptyInventory)
        }

        return Promise.resolve(emptyInventory)
      }

      if (Array.isArray(state.modules) && state.modules.length > 0) {
        return Promise.resolve({
          modules: state.modules,
          menuItems: state.menuModules,
        })
      }

      if (modulesListInFlight) return modulesListInFlight

      modulesListInFlight = import("@/api/clients/admin.client")
        .then(({ adminClient }) => adminClient.getModules())
        .then((inventory) => {
          if (
            !inventory ||
            !Array.isArray(inventory.modules) ||
            !Array.isArray(inventory.menuItems)
          ) {
            throw new Error("[App] Invalid normalized modules inventory")
          }

          commit("SET_MODULES_LIST", inventory)
          return inventory
        })
        .finally(() => {
          modulesListInFlight = null
        })

      return modulesListInFlight
    },
    setDevMode({ commit }) {
      commit("SET_DEV_MODE")
    },
    setEditorDevMode({ commit }) {
      commit("SET_EDITOR_DEV_MODE")
    },
    setEditorProdMode({ commit }) {
      commit("SET_EDITOR_PROD_MODE")
    },
    setLocalMode({ commit }) {
      commit("SET_LOCAL_MODE")
    },
  },
}
